import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Icon, Grid } from 'semantic-ui-react';
import styled from 'styled-components';
import { createLenderInsurance } from '../../../services/lender-insurance';
import { getEstates } from '../../../services/broker';
import { successNotification, dangerNotification, warningNotification } from '../../../services/notification';
import { maskCpf, maskCnpj, maskZipCode, maskPhoneNumber } from '../../../services/masks';
import useDebounce from '../../../services/useDebounce';

const FormSection = styled.div`
  margin-bottom: 24px;
  padding: 20px;
  background: transparent;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
`;

const SectionTitle = styled.h4`
  color: #59249B;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
`;

interface EstateOption {
  id: string;
  name: string;
}

interface CreateLenderInsuranceModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  estateId?: string;
  brokerId?: string;
  estates?: EstateOption[];
}

const CreateLenderInsuranceModal: React.FC<CreateLenderInsuranceModalProps> = ({
  open,
  onClose,
  onSuccess,
  estateId,
  brokerId,
  estates = []
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingEstates, setLoadingEstates] = useState(false);
  const [selectedEstateId, setSelectedEstateId] = useState(estateId || '');
  const [estateOptions, setEstateOptions] = useState(
    estates.map(e => ({ key: e.id, value: e.id, text: e.name }))
  );

  // Determine if we need to show estate selector (broker mode)
  const showEstateSelector = !estateId;

  useEffect(() => {
    if (showEstateSelector && brokerId) {
      setLoadingEstates(true);
      getEstates(brokerId)
        .then(data => {
          const filtered = data
            .filter(e => e.lender_insurance_enabled)
            .map(e => ({ key: e.id, value: e.id, text: e.name }));
          setEstateOptions(filtered);
        })
        .catch(err => {
          console.error('Error loading estates', err);
          warningNotification('Erro', 'Não foi possível carregar a lista de imobiliárias.');
        })
        .finally(() => setLoadingEstates(false));
    }
  }, [showEstateSelector, brokerId]);

  // Tenant fields
  const [tenantName, setTenantName] = useState('');
  const [tenantDocument, setTenantDocument] = useState('');
  const [tenantEmail, setTenantEmail] = useState('');
  const [tenantPhone, setTenantPhone] = useState('');

  // Owner fields
  const [ownerName, setOwnerName] = useState('');
  const [ownerDocument, setOwnerDocument] = useState('');

  // Property fields
  const [propertyZipcode, setPropertyZipcode] = useState('');
  const [propertyStreet, setPropertyStreet] = useState('');
  const [propertyNumber, setPropertyNumber] = useState('');
  const [propertyComplement, setPropertyComplement] = useState('');
  const [propertyNeighborhood, setPropertyNeighborhood] = useState('');
  const [propertyCity, setPropertyCity] = useState('');
  const [propertyState, setPropertyState] = useState('');
  const [loadingCep, setLoadingCep] = useState(false);

  // Debounce CEP for auto-fetch
  const debouncedCep = useDebounce<string>(propertyZipcode.replace(/\D/g, ''), 500);

  // Value
  const [rentValue, setRentValue] = useState('');

  // Auto-fetch address when CEP changes
  useEffect(() => {
    const fetchAddress = async () => {
      if (debouncedCep.length === 8) {
        setLoadingCep(true);
        try {
          const response = await fetch(`https://viacep.com.br/ws/${debouncedCep}/json/`);
          const data = await response.json();
          if (data.erro) {
            warningNotification('CEP não encontrado', 'Verifique o CEP informado e tente novamente.');
            setPropertyStreet('');
            setPropertyNeighborhood('');
            setPropertyCity('');
            setPropertyState('');
          } else {
            setPropertyStreet(data.logradouro || '');
            setPropertyNeighborhood(data.bairro || '');
            setPropertyCity(data.localidade || '');
            setPropertyState(data.uf || '');
          }
        } catch (err) {
          warningNotification('Erro ao buscar CEP', 'Não foi possível buscar o endereço.');
        } finally {
          setLoadingCep(false);
        }
      }
    };

    fetchAddress();
  }, [debouncedCep]);

  const resetForm = () => {
    setTenantName('');
    setTenantDocument('');
    setTenantEmail('');
    setTenantPhone('');
    setOwnerName('');
    setOwnerDocument('');
    setPropertyZipcode('');
    setPropertyStreet('');
    setPropertyNumber('');
    setPropertyComplement('');
    setPropertyNeighborhood('');
    setPropertyCity('');
    setPropertyState('');
    setRentValue('');
    if (!estateId) {
      setSelectedEstateId('');
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const formatDocument = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 11) {
      return maskCpf(cleaned);
    }
    return maskCnpj(cleaned);
  };

  const parseMoneyToNumber = (value: string): number => {
    const cleaned = value.replace(/\D/g, '');
    return parseInt(cleaned, 10) / 100 || 0;
  };

  const formatMoney = (value: string): string => {
    const cleaned = value.replace(/\D/g, '');
    const number = parseInt(cleaned, 10) / 100 || 0;
    return number.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  // CPF/CNPJ validation functions
  const validateCpf = (cpf: string): boolean => {
    const cleaned = cpf.replace(/\D/g, '');
    if (cleaned.length !== 11 || /^(\d)\1+$/.test(cleaned)) return false;
    let sum = 0;
    for (let i = 0; i < 9; i++) sum += parseInt(cleaned.charAt(i)) * (10 - i);
    let rest = (sum * 10) % 11;
    if (rest === 10 || rest === 11) rest = 0;
    if (rest !== parseInt(cleaned.charAt(9))) return false;
    sum = 0;
    for (let i = 0; i < 10; i++) sum += parseInt(cleaned.charAt(i)) * (11 - i);
    rest = (sum * 10) % 11;
    if (rest === 10 || rest === 11) rest = 0;
    return rest === parseInt(cleaned.charAt(10));
  };

  const validateCnpj = (cnpj: string): boolean => {
    const cleaned = cnpj.replace(/\D/g, '');
    if (cleaned.length !== 14 || /^(\d)\1+$/.test(cleaned)) return false;
    const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    let sum = 0;
    for (let i = 0; i < 12; i++) sum += parseInt(cleaned.charAt(i)) * weights1[i];
    let rest = sum % 11;
    if (rest < 2) rest = 0; else rest = 11 - rest;
    if (rest !== parseInt(cleaned.charAt(12))) return false;
    sum = 0;
    for (let i = 0; i < 13; i++) sum += parseInt(cleaned.charAt(i)) * weights2[i];
    rest = sum % 11;
    if (rest < 2) rest = 0; else rest = 11 - rest;
    return rest === parseInt(cleaned.charAt(13));
  };

  const validateDocument = (doc: string): boolean => {
    const cleaned = doc.replace(/\D/g, '');
    return cleaned.length === 11 ? validateCpf(doc) : cleaned.length === 14 ? validateCnpj(doc) : false;
  };

  const isFormValid = () => {
    const hasValidEstate = estateId || selectedEstateId;
    return (
      hasValidEstate &&
      tenantName.trim() &&
      tenantDocument.replace(/\D/g, '').length >= 11 &&
      ownerName.trim() &&
      ownerDocument.replace(/\D/g, '').length >= 11 &&
      propertyZipcode.replace(/\D/g, '').length === 8 &&
      propertyStreet.trim() &&
      propertyNumber.trim() &&
      propertyNeighborhood.trim() &&
      propertyCity.trim() &&
      propertyState.trim() &&
      parseMoneyToNumber(rentValue) > 0
    );
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      dangerNotification('Erro', 'Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    // Validate documents
    if (!validateDocument(tenantDocument)) {
      dangerNotification('Erro', 'CPF ou CNPJ do locatário inválido.');
      return;
    }
    if (!validateDocument(ownerDocument)) {
      dangerNotification('Erro', 'CPF ou CNPJ do proprietário inválido.');
      return;
    }

    const finalEstateId = estateId || selectedEstateId;
    if (!finalEstateId) {
      dangerNotification('Erro', 'Por favor, selecione uma imobiliária.');
      return;
    }

    setLoading(true);
    try {
      // Build full address from separate fields
      const fullAddress = `${propertyStreet}, ${propertyNumber}${propertyComplement ? `, ${propertyComplement}` : ''} - ${propertyNeighborhood}`;

      await createLenderInsurance({
        estateId: finalEstateId,
        tenantName,
        tenantDocument: tenantDocument.replace(/\D/g, ''),
        tenantEmail: tenantEmail || undefined,
        tenantPhone: tenantPhone.replace(/\D/g, '') || undefined,
        ownerName,
        ownerDocument: ownerDocument.replace(/\D/g, ''),
        propertyAddress: fullAddress,
        propertyZipcode: propertyZipcode.replace(/\D/g, ''),
        propertyCity,
        propertyState,
        rentValue: parseMoneyToNumber(rentValue),
        sourceType: 'STANDALONE'
      });

      successNotification('Sucesso', 'Seguro Prestamista criado com sucesso!');
      resetForm();
      onSuccess();
      onClose();
    } catch (error) {
      const err = error as any;
      dangerNotification('Erro', err.message || 'Erro ao criar seguro prestamista.');
    } finally {
      setLoading(false);
    }
  };

  const stateOptions = [
    { key: 'AC', value: 'AC', text: 'AC - Acre' },
    { key: 'AL', value: 'AL', text: 'AL - Alagoas' },
    { key: 'AP', value: 'AP', text: 'AP - Amapá' },
    { key: 'AM', value: 'AM', text: 'AM - Amazonas' },
    { key: 'BA', value: 'BA', text: 'BA - Bahia' },
    { key: 'CE', value: 'CE', text: 'CE - Ceará' },
    { key: 'DF', value: 'DF', text: 'DF - Distrito Federal' },
    { key: 'ES', value: 'ES', text: 'ES - Espírito Santo' },
    { key: 'GO', value: 'GO', text: 'GO - Goiás' },
    { key: 'MA', value: 'MA', text: 'MA - Maranhão' },
    { key: 'MT', value: 'MT', text: 'MT - Mato Grosso' },
    { key: 'MS', value: 'MS', text: 'MS - Mato Grosso do Sul' },
    { key: 'MG', value: 'MG', text: 'MG - Minas Gerais' },
    { key: 'PA', value: 'PA', text: 'PA - Pará' },
    { key: 'PB', value: 'PB', text: 'PB - Paraíba' },
    { key: 'PR', value: 'PR', text: 'PR - Paraná' },
    { key: 'PE', value: 'PE', text: 'PE - Pernambuco' },
    { key: 'PI', value: 'PI', text: 'PI - Piauí' },
    { key: 'RJ', value: 'RJ', text: 'RJ - Rio de Janeiro' },
    { key: 'RN', value: 'RN', text: 'RN - Rio Grande do Norte' },
    { key: 'RS', value: 'RS', text: 'RS - Rio Grande do Sul' },
    { key: 'RO', value: 'RO', text: 'RO - Rondônia' },
    { key: 'RR', value: 'RR', text: 'RR - Roraima' },
    { key: 'SC', value: 'SC', text: 'SC - Santa Catarina' },
    { key: 'SP', value: 'SP', text: 'SP - São Paulo' },
    { key: 'SE', value: 'SE', text: 'SE - Sergipe' },
    { key: 'TO', value: 'TO', text: 'TO - Tocantins' }
  ];

  return (
    <Modal open={open} onClose={handleClose} size="small">
      <Modal.Header>
        <Icon name="shield" style={{ color: '#59249B' }} /> Novo Seguro Prestamista
      </Modal.Header>

      <Modal.Content scrolling>
        <Form>
          {/* Estate Selector for Admins/Brokers - Only shown when estateId is not provided */}
          {showEstateSelector && (
            <FormSection>
              <SectionTitle>
                <Icon name="building outline" /> Imobiliária
              </SectionTitle>
              <Form.Select
                fluid
                loading={loadingEstates}
                disabled={loadingEstates}
                label="Selecione a Imobiliária *"
                placeholder="Escolha uma imobiliária"
                options={estateOptions}
                value={selectedEstateId}
                onChange={(_, { value }) => setSelectedEstateId(value as string)}
                search
              />
            </FormSection>
          )}

          {/* Tenant Section */}
          <FormSection>
            <SectionTitle>
              <Icon name="user" /> Dados do Locatário
            </SectionTitle>
            <Grid>
              <Grid.Row>
                <Grid.Column width={8}>
                  <Form.Input
                    label="Nome *"
                    placeholder="Nome completo"
                    value={tenantName}
                    onChange={(e) => setTenantName(e.target.value)}
                  />
                </Grid.Column>
                <Grid.Column width={8}>
                  <Form.Input
                    label="CPF/CNPJ *"
                    placeholder="000.000.000-00"
                    value={tenantDocument}
                    onChange={(e) => setTenantDocument(formatDocument(e.target.value))}
                    maxLength={18}
                  />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column width={8}>
                  <Form.Input
                    label="E-mail"
                    placeholder="email@exemplo.com"
                    type="email"
                    value={tenantEmail}
                    onChange={(e) => setTenantEmail(e.target.value)}
                  />
                </Grid.Column>
                <Grid.Column width={8}>
                  <Form.Input
                    label="Telefone"
                    placeholder="(00) 00000-0000"
                    value={tenantPhone}
                    onChange={(e) => setTenantPhone(maskPhoneNumber(e.target.value))}
                    maxLength={15}
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </FormSection>

          {/* Owner Section */}
          <FormSection>
            <SectionTitle>
              <Icon name="building" /> Dados do Proprietário
            </SectionTitle>
            <Grid>
              <Grid.Row>
                <Grid.Column width={8}>
                  <Form.Input
                    label="Nome *"
                    placeholder="Nome completo"
                    value={ownerName}
                    onChange={(e) => setOwnerName(e.target.value)}
                  />
                </Grid.Column>
                <Grid.Column width={8}>
                  <Form.Input
                    label="CPF/CNPJ *"
                    placeholder="000.000.000-00"
                    value={ownerDocument}
                    onChange={(e) => setOwnerDocument(formatDocument(e.target.value))}
                    maxLength={18}
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </FormSection>

          {/* Property Section */}
          <FormSection>
            <SectionTitle>
              <Icon name="home" /> Dados do Imóvel
            </SectionTitle>
            <Grid>
              {/* CEP First - with auto-fetch */}
              <Grid.Row>
                <Grid.Column width={5}>
                  <Form.Input
                    label="CEP *"
                    placeholder="00000-000"
                    value={propertyZipcode}
                    onChange={(e) => setPropertyZipcode(maskZipCode(e.target.value))}
                    maxLength={9}
                    loading={loadingCep}
                    icon={loadingCep ? undefined : 'search'}
                  />
                </Grid.Column>
              </Grid.Row>

              {/* Street, Number, Complement */}
              <Grid.Row>
                <Grid.Column width={8}>
                  <Form.Input
                    label="Rua/Logradouro *"
                    placeholder="Nome da rua"
                    value={propertyStreet}
                    onChange={(e) => setPropertyStreet(e.target.value)}
                    loading={loadingCep}
                    disabled={loadingCep}
                  />
                </Grid.Column>
                <Grid.Column width={3}>
                  <Form.Input
                    label="Número *"
                    placeholder="Nº"
                    value={propertyNumber}
                    onChange={(e) => setPropertyNumber(e.target.value)}
                  />
                </Grid.Column>
                <Grid.Column width={5}>
                  <Form.Input
                    label="Complemento"
                    placeholder="Apto, Bloco, etc."
                    value={propertyComplement}
                    onChange={(e) => setPropertyComplement(e.target.value)}
                  />
                </Grid.Column>
              </Grid.Row>

              {/* Neighborhood, City, State */}
              <Grid.Row>
                <Grid.Column width={5}>
                  <Form.Input
                    label="Bairro *"
                    placeholder="Bairro"
                    value={propertyNeighborhood}
                    onChange={(e) => setPropertyNeighborhood(e.target.value)}
                    loading={loadingCep}
                    disabled={loadingCep}
                  />
                </Grid.Column>
                <Grid.Column width={6}>
                  <Form.Input
                    label="Cidade *"
                    placeholder="Cidade"
                    value={propertyCity}
                    onChange={(e) => setPropertyCity(e.target.value)}
                    loading={loadingCep}
                    disabled={loadingCep}
                  />
                </Grid.Column>
                <Grid.Column width={5}>
                  <Form.Select
                    label="Estado *"
                    placeholder="UF"
                    options={stateOptions}
                    value={propertyState}
                    onChange={(e, { value }) => setPropertyState(value as string)}
                    disabled={loadingCep}
                    fluid
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </FormSection>

          {/* Value Section */}
          <FormSection>
            <SectionTitle>
              <Icon name="dollar" /> Valor
            </SectionTitle>
            <Grid>
              <Grid.Row>
                <Grid.Column width={8}>
                  <Form.Input
                    label="Valor do Aluguel *"
                    placeholder="R$ 0,00"
                    value={rentValue}
                    onChange={(e) => setRentValue(formatMoney(e.target.value))}
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </FormSection>
        </Form>
      </Modal.Content>

      <Modal.Actions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          color="purple"
          onClick={handleSubmit}
          loading={loading}
          disabled={!isFormValid()}
        >
          <Icon name="check" /> Criar Seguro
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default CreateLenderInsuranceModal;
